import { Component, ViewEncapsulation, OnInit, OnDestroy, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { FuseDrawerComponent } from '@fuse/components/drawer';
import { FuseDrawerService } from '@fuse/components/drawer';
import { Subject } from 'rxjs';
import { HoleType, PlayerSlot } from '../booking.types';
import { BookingStateService } from '../booking-state.service';

@Component({
    selector: 'golf-cart-drawer',
    templateUrl: './golf-cart-drawer.component.html',
    styles: [
        `
            golf-cart-drawer {
                position: static;
                display: block;
                flex: none;
                width: auto;
            }
        `,
    ],
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        FormsModule,
        MatIconModule,
        FuseDrawerComponent,
        MatButtonModule,
        MatTooltipModule,
        MatCardModule,
        MatDividerModule,
        MatChipsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        MatProgressSpinnerModule,
        MatRadioModule,
    ],
})
export class GolfCartDrawerComponent implements OnInit, OnDestroy {
    selectedHoleType: HoleType = HoleType.EighteenHoles;
    selectedPayingPlayers: PlayerSlot[] = [];
    availablePlayers: PlayerSlot[] = [];

    HoleType = HoleType;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    selectedGolfCart: PlayerSlot[] = [];

    constructor(
        private _fuseDrawerService: FuseDrawerService,
        private _bookingStateService: BookingStateService,
    ) {
        effect(() => {
            this.selectedGolfCart = this._bookingStateService.selectedGolfCart();
            this.selectedHoleType = this._bookingStateService.holeType();
            const slots = this._bookingStateService.slots() || [];

            if (slots && slots.length > 0) {
                this.availablePlayers = slots.filter(s => s.type !== 'EMPTY' && s.type !== 'GUEST');
                const currentUser = this.availablePlayers.find(player => player.isCurrentUser);

                if (currentUser) {
                    this.selectedPayingPlayers = [currentUser];
                } else {
                    // Fallback: select all available players if no current user identified
                    this.selectedPayingPlayers = [...slots];
                }
            }
        })
    }

    ngOnInit(): void {

    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    get selectedPrice(): number {
        const course = this._bookingStateService.course();
        const holeType = this.selectedHoleType;

        if (!course || !holeType) {
            return 0;
        }

        if (holeType === HoleType.NineHoles) {
            return course.pricePerGolfCartNineHoles;
        }

        return course.pricePerGolfCartEighteenHoles
    }

    get pricePerPlayer(): number {
        if (this.selectedPayingPlayers.length === 0) return 0;
        return this.selectedPrice / this.selectedPayingPlayers.length;
    }

    get holeTypeDisplayName(): string {
        return this.selectedHoleType === HoleType.NineHoles ? '9 Holes' : '18 Holes';
    }

    get courseName(): string {
        const course = this._bookingStateService.course();
        return course ? course.name : '';
    }

    togglePlayerSelection(player: PlayerSlot): void {
        const index = this.selectedPayingPlayers.findIndex(p => p.id === player.id);

        if (index > -1) {
            this.selectedPayingPlayers.splice(index, 1);
        } else {
            this.selectedPayingPlayers.push(player);
        }
    }

    isPlayerSelected(player: PlayerSlot): boolean {
        return this.selectedPayingPlayers.some(p => p.id === player.id);
    }

    getUserName(user: PlayerSlot): string {
        return `${user.displayName}`.trim();
    }

    getUserInitials(user: PlayerSlot): string {
        const split = user.displayName?.trim().split(' ') || [];
        return split.length >= 2
            ? (split[0].charAt(0).toUpperCase() + split[1].charAt(0).toUpperCase())
            : (split[0]?.charAt(0)?.toUpperCase() || '?');
    }

    getUserImage(user: PlayerSlot): string | null {
        return user.profileImageUrl || null;
    }

    canConfirmSelection(): boolean {
        return this.selectedPayingPlayers.length > 0;
    }

    confirmSelection(): void {
        if (!this.canConfirmSelection()) {
            return;
        }

        this._bookingStateService.golfCarts.update((golfCarts) => {
            return [...golfCarts, this.selectedPayingPlayers];
        });
        this.closeDrawer();
    }

    closeDrawer(): void {
        const drawer = this._fuseDrawerService.getComponent('golfCartDrawer');
        if (drawer) {
            drawer.close();
        }
    }
}